import React, { useLayoutEffect, useCallback, useState, useMemo } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { range } from 'utils/commonUtils'
import { OptionItem } from '../common'
import { TROUBLE_DATE_FORMAT } from '../../../constants/qnState'
import Text from '../texts/Text'
import PullDownPicker from './PullDownPicker'

// 変形フォーマットプラグインを追加
dayjs.extend(advancedFormat)

export type TimePickerProps = {
  /** 時間(YYYY/MM/DD:HH) */
  time: string
  /** 選択可能な時間の最小値(YYYY/MM/DD:HH) */
  minTime?: string
  /** 値が変更された時のコールバック */
  onChanged?: (time: string) => void
}

/** 年の選択肢 */
const yearOptions: OptionItem<number>[] = range(
  2000,
  Number(dayjs().format('YYYY'))
).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 月の選択肢 */
const monthOptions: OptionItem<number>[] = range(1, 12).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 日の選択肢 */
const dateOptions: OptionItem<number>[] = range(1, 31).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 時の選択肢 */
const hourOptions: OptionItem<number>[] = range(0, 23).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))

/** 時間を選択するコンポーネント */
const DatePicker = (props: TimePickerProps): JSX.Element => {
  const { time, minTime, onChanged } = props
  const [year, month, date, hour] = useMemo(() => {
    const djTime = dayjs(time)
    return [
      djTime.year() ? djTime.year() : yearOptions[0].value,
      djTime.month() ? djTime.month() + 1 : monthOptions[0].value,
      djTime.date() ? djTime.date() : dateOptions[0].value,
      djTime.hour() ? djTime.hour() : hourOptions[0].value,
    ]
  }, [time])
  // 最小時間
  const [minYear, minMonth, minDate, minHour] = useMemo(() => {
    const djTime = minTime ? dayjs(minTime) : undefined
    return djTime
      ? [djTime.year(), djTime.month() + 1, djTime.date(), djTime.hour()]
      : [
          yearOptions[0].value,
          monthOptions[0].value,
          dateOptions[0].value,
          hourOptions[0].value,
        ]
  }, [minTime])

  const [isPickerOpenedYear, setIsPickerOpenedYear] = useState(false)
  const [isPickerOpenedMonth, setIsPickerOpenedMonth] = useState(false)
  const [isPickerOpenedDate, setIsPickerOpenedDate] = useState(false)
  const [isPickerOpenedHour, setIsPickerOpenedHour] = useState(false)

  const [yearItems, setYearItems] = useState<OptionItem<number>[]>([])
  const [monthItems, setMonthItems] = useState<OptionItem<number>[]>([])
  const [dateItems, setDateItems] = useState<OptionItem<number>[]>([])
  const [hourItems, setHourItems] = useState<OptionItem<number>[]>([])

  const [selectedYear, setSelectedYear] = useState(year)
  const [selectedMonth, setSelectedMonth] = useState(month)
  const [selectedDate, setSelectedDate] = useState(date)
  const [selectedHour, setSelectedHour] = useState(hour)

  // selectedまでスクロールする処理
  const scrollDownToSelect = (selectedValue: number): void => {
    setTimeout(() => {
      const selectedElement = document.querySelector(
        `[data-year-value="${selectedValue}"]`
      )
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center' })
      }
    }, 0)
  }

  // 年月日時をクリックした際に プルダウン表示 & selected までスクロール
  const handleDateClickYear = useCallback(() => {
    setYearItems(yearOptions.filter((x) => x.value >= minYear))
    setSelectedYear(selectedYear <= minYear ? minYear : selectedYear)
    setIsPickerOpenedYear(true)
    scrollDownToSelect(selectedYear)
  }, [yearOptions, minTime, selectedYear])

  const handleDateClickMonth = useCallback(() => {
    setIsPickerOpenedMonth(true)
    scrollDownToSelect(selectedMonth)
  }, [monthOptions, minTime, selectedMonth])

  const handleDateClickDate = useCallback(() => {
    setIsPickerOpenedDate(true)
    scrollDownToSelect(selectedDate)
  }, [dateOptions, minTime, selectedDate])

  const handleDateClickHour = useCallback(() => {
    setIsPickerOpenedHour(true)
    scrollDownToSelect(selectedHour)
  }, [hourOptions, minTime, selectedHour])

  // 選択されたものを更新しプルダウンを閉じる処理
  const handleYearChanged = useCallback(
    (yearItem): void => {
      setSelectedYear(yearItem.value)
      setIsPickerOpenedYear(false)
    },
    [selectedYear]
  )
  const handleMonthChanged = useCallback(
    (monthItem): void => {
      setSelectedMonth(monthItem.value)
      setIsPickerOpenedMonth(false)
    },
    [selectedMonth]
  )
  const handleDateChanged = useCallback(
    (dateItem): void => {
      setSelectedDate(dateItem.value)
      setIsPickerOpenedDate(false)
    },
    [selectedDate]
  )
  const handleHourChanged = useCallback(
    (hourItem): void => {
      setSelectedHour(hourItem.value)
      setIsPickerOpenedHour(false)
    },
    [selectedHour]
  )

  // 日付を切り替えたときにTimeの選択肢を生成する
  useLayoutEffect(() => {
    if (minTime) {
      const currentMinYear = minYear > selectedYear ? minYear : selectedYear
      const currentMinMonth =
        minMonth > selectedMonth ? minMonth : selectedMonth
      const currentMinDate = minDate > selectedDate ? minDate : selectedDate
      const currentMinHour = minHour > selectedHour ? minHour : selectedHour

      props.onChanged &&
        props.onChanged(
          `${String(currentMinYear)}/${String(currentMinMonth).padStart(
            2,
            '0'
          )}/${String(currentMinDate).padStart(2, '0')}:${String(
            currentMinHour
          ).padStart(2, '0')}`
        )
      return
    }
  }, [
    minTime,
    handleDateClickYear,
    handleDateClickMonth,
    handleDateClickDate,
    handleDateClickHour,
  ])

  // 存在しない日付をドラムロールの選択肢の表示しない
  const validateDate = (): void => {
    // 現在の年・月を取得
    const currentYearMonth = `${String(selectedYear)}/${String(
      selectedMonth
    ).padStart(2, '0')}/${String(1).padStart(2, '0')}:${String(1).padStart(
      2,
      '0'
    )}`
    // 年・月からその月の月末を割り出す
    const maxDate = dayjs(
      dayjs(currentYearMonth).endOf('month').format(TROUBLE_DATE_FORMAT)
    ).date()
    // ドラムの選択肢に月末を最大値として設定
    setDateItems(
      dateOptions.filter((x) => maxDate >= x.value && x.value >= minDate)
    )
    // ドラムの選択値が最大値異常の場合、最大値を選択値とする
    setSelectedDate(maxDate <= selectedDate ? maxDate : selectedDate)
  }

  // 未来の日付をドラムロールの選択肢に表示しない
  useLayoutEffect(() => {
    // 現在選択中の日付を取得
    const currentTime = `${String(selectedYear)}/${String(
      selectedMonth
    ).padStart(2, '0')}/${String(selectedDate).padStart(2, '0')}:${String(
      selectedHour
    ).padStart(2, '0')}`
    // 現在の日付を取得
    const now = dayjs().format(TROUBLE_DATE_FORMAT)

    // 各日付単位ごとに現在時刻以上かをチェック
    const isYearOver = dayjs(now).year() <= dayjs(currentTime).year()
    const isMonthOver = dayjs(now).month() <= dayjs(currentTime).month()
    const isDateOver = dayjs(now).date() <= dayjs(currentTime).date()
    const isHourOver = dayjs(now).hour() <= dayjs(currentTime).hour()

    // 年の最大値は現在の日付から取得しているためここでは処理しない
    // 年が現在の年以上の場合、月の選択肢の最大値を現在の月とする
    setMonthItems(
      monthOptions.filter((x) =>
        isYearOver
          ? Number(dayjs(now).month() + 1) >= x.value && x.value >= minMonth
          : x.value >= minMonth
      )
    )
    // 年月が現在の年月以上の場合、月の選択値を現在の月とする
    setSelectedMonth(
      isYearOver && isMonthOver
        ? dayjs(now).month() + 1
        : selectedMonth <= minMonth
        ? minMonth
        : selectedMonth
    )
    // 年月日が現在の年月日以上の場合、日の選択値を現在の日とする
    // 上記に該当しない場合は日付の存在チェックを行う
    isYearOver && isMonthOver && isDateOver
      ? setSelectedDate(dayjs(now).date())
      : validateDate()

    // 年月が現在の年月以上の場合、日の選択肢の最大値を現在の日とする
    // 上記に該当しない場合は日付の存在チェックを行う
    isYearOver && isMonthOver
      ? setDateItems(
          dateOptions.filter(
            (x) => Number(dayjs(now).date()) >= x.value && x.value >= minDate
          )
        )
      : validateDate()
    // 年月日が現在の年月日以上の場合、時刻の選択肢の最大値を現在の時刻とする
    setHourItems(
      hourOptions.filter((x) =>
        isYearOver && isMonthOver && isDateOver
          ? Number(dayjs(now).hour()) >= x.value && x.value >= minHour
          : x.value >= minHour
      )
    )
    // 年月日時刻が現在の年月日時刻以上の場合、時刻の選択値を現在の時刻とする
    setSelectedHour(
      isYearOver && isMonthOver && isDateOver && isHourOver
        ? dayjs(now).hour()
        : selectedHour <= minHour
        ? minHour
        : selectedHour
    )
  }, [selectedYear, selectedMonth, selectedDate, selectedHour, onChanged])

  return (
    <ReservationTimeSelectionWrap>
      <TimeContainer>
        <TimeContainerContent onClick={handleDateClickYear}>
          {selectedYear.toString().padStart(2, '0')}
        </TimeContainerContent>
        <StyledText>年</StyledText>
        {isPickerOpenedYear && (
          <PullDownPicker
            options={yearItems}
            selectedValue={selectedYear}
            handleValueChanged={handleYearChanged}
          />
        )}
      </TimeContainer>
      <TimeContainer>
        <TimeContainerContent onClick={handleDateClickMonth}>
          {selectedMonth.toString().padStart(2, '0')}
        </TimeContainerContent>
        <StyledText>月</StyledText>
        {isPickerOpenedMonth && (
          <PullDownPicker
            options={monthItems}
            selectedValue={selectedMonth}
            handleValueChanged={handleMonthChanged}
          />
        )}
      </TimeContainer>
      <TimeContainer>
        <TimeContainerContent onClick={handleDateClickDate}>
          {selectedDate.toString().padStart(2, '0')}
        </TimeContainerContent>
        <StyledText>日</StyledText>
        {isPickerOpenedDate && (
          <PullDownPicker
            options={dateItems}
            selectedValue={selectedDate}
            handleValueChanged={handleDateChanged}
          />
        )}
      </TimeContainer>
      <TimeContainer>
        <TimeContainerContent onClick={handleDateClickHour}>
          {selectedHour.toString().padStart(2, '0')}
        </TimeContainerContent>
        <StyledText>時頃</StyledText>
        {isPickerOpenedHour && (
          <PullDownPicker
            options={hourItems}
            selectedValue={selectedHour}
            handleValueChanged={handleHourChanged}
          />
        )}
      </TimeContainer>
    </ReservationTimeSelectionWrap>
  )
}

export default React.memo(DatePicker)

const ReservationTimeSelectionWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
`
const TimeContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`
const StyledText = styled(Text)`
  margin-left: 1px;
  white-space: nowrap;
`
const TimeContainerContent = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 25px;
  padding: 2px 12px 2px 4px;
  border: 1px solid #707070;
  // プルダウン三角アイコン
  position: relative;
  &::before {
    content: '';
    border-right: 3px solid transparent;
    border-bottom: 6px solid #333;
    border-left: 3px solid transparent;
    position: absolute;
    right: 4px;
    top: 26%;
    transform: translate(0 -50%);
  }
  &::after {
    content: '';
    border-right: 3px solid transparent;
    border-top: 6px solid #333;
    border-left: 3px solid transparent;
    position: absolute;
    right: 4px;
    bottom: 26%;
    transform: translate(0 -50%);
  }
`
